<script setup>
import { computed } from 'vue';
import Draggable from 'vuedraggable';
import KanbanColumn from './KanbanColumn.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  stages: { type: Array, default: () => [] },
  columns: { type: Object, default: () => ({}) },
  isLoading: { type: Boolean, default: false },
  showValue: { type: Boolean, default: false },
  currency: { type: String, default: 'BRL' },
  canManage: { type: Boolean, default: false },
  // CRM-01h fase 3: seleção do "mover em bloco".
  selectionActive: { type: Boolean, default: false },
  isCardSelected: { type: Function, default: () => false },
  isStageSelected: { type: Function, default: () => false },
});

const emit = defineEmits([
  'drop',
  'editStage',
  'addStage',
  'openCard',
  'reorderStages',
  'toggleCard',
  'toggleStage',
  'loadMore',
]);

const isEmpty = computed(() => !props.stages.length);

// vuedraggable mutates the bound array, so give it a local copy and report the new
// order upward rather than writing back into the store's pipeline object.
const stageList = computed({
  get: () => props.stages,
  set: value =>
    emit(
      'reorderStages',
      value.map((stage, index) => ({ id: stage.id, position: index }))
    ),
});
</script>

<template>
  <div class="flex-1 min-h-0 overflow-x-auto pz-scrollbar">
    <div
      v-if="isLoading && isEmpty"
      class="flex items-center justify-center h-full"
    >
      <Spinner class="text-n-brand" />
    </div>

    <div v-else class="flex h-full gap-3 p-3 min-w-min">
      <Draggable
        v-model="stageList"
        item-key="id"
        class="flex h-full gap-3"
        handle=".kanban-stage-handle"
        ghost-class="opacity-40"
        :disabled="!canManage"
      >
        <template #item="{ element }">
          <KanbanColumn
            :stage="element"
            :cards="columns[element.id]?.cards || []"
            :total-count="columns[element.id]?.totalCount || 0"
            :show-value="showValue"
            :currency="currency"
            :can-manage="canManage"
            :next-cursor="columns[element.id]?.nextCursor || null"
            :loading-more="Boolean(columns[element.id]?.loadingMore)"
            :selection-active="selectionActive"
            :stage-selected="isStageSelected(element.id)"
            :is-card-selected="isCardSelected"
            @drop="emit('drop', $event)"
            @edit-stage="emit('editStage', $event)"
            @open-card="emit('openCard', $event)"
            @toggle-card="emit('toggleCard', $event)"
            @toggle-stage="emit('toggleStage', $event)"
            @load-more="emit('loadMore', $event)"
          />
        </template>
      </Draggable>

      <button
        v-if="canManage"
        class="flex-shrink-0 w-[300px] h-12 text-sm rounded-lg border border-dashed border-n-weak text-n-slate-11 hover:border-n-brand hover:text-n-brand"
        @click="emit('addStage')"
      >
        + {{ $t('KANBAN.COLUMN.ADD') }}
      </button>
    </div>
  </div>
</template>
